import { Icon, IContextualMenuItem, IIconStyles } from "@fluentui/react";
import { getFileTypeIconProps } from "@fluentui/react-file-type-icons";
import React from "react";
import { useSelector } from "react-redux";
import { newTemplateFilterEnum } from "../../../src/taskpane/components/enums/newTemplateFilterEnum";
import { FolderViewWrapper } from "../../../src/taskpane/components/helpers/folderViewWrapper";
import { ComponentTypes } from "../../../src/taskpane/config/config";
import { DocubirdCache, SharePointFolderContentTypeIds } from "../../../src/taskpane/config/constants";
import { RootState } from "../../../src/taskpane/redux";
import { LanguageContext } from "../../../utilities/LocalizationModule";
import { NewDocumentTemplate } from "../../../src/taskpane/interfaces/IDocumentTemplate";
import { CacheHelper } from "../../../utilities/CacheHelper";
import { DatasourceFieldHelper } from "../../../src/taskpane/components/helpers/dataSourceFieldHelper";
import { useClientTypeContext } from "../../../src/taskpane/components/hooks/useClientTypeContext";
import { menuItemIconStyles } from "../../../src/taskpane/config/controlStyles";
import { DocumentAdd20Regular } from "../../../src/taskpane/components/_v2/components/SvgIcons/DocumentAdd20Regular";
import { getFolderPermissionMenuState, runFolderPermissionMenuAction } from "./folderPermissionMenuState";
import { getCurrentTemplateContentType, isTemplateFilterAllowed, rootContentType } from "./templateFilterHelpers";

const iconStyles: IIconStyles = {
    root: {
        display: "flex",
        marginLeft: 4,
        marginRight: 4
    }
}

const getDatasourceTemplates = (): NewDocumentTemplate[] => {
    const dsDocTemplates = CacheHelper.get(DocubirdCache.datasourceDocumentTemplates);
    if (Array.isArray(dsDocTemplates)) {
        return dsDocTemplates as NewDocumentTemplate[];
    }
    return [];
}

export function useMenuItem_NewDatasourceDocumentTemplate(
    isEnabled: boolean,
    waitFor: boolean,
    newDocumentTemplates: NewDocumentTemplate[],
    openCreateTemplateModal: (selectedTemplate: NewDocumentTemplate) => void
) {
    const languageStrings = React.useContext(LanguageContext);

    const currentSharepointFolder = useSelector((state: RootState) => state.currentSharepointFolder);
    const isBusySaving = useSelector((state: RootState) => state.savingInBackground.isBusySaving);
    const workingDirectories = useSelector((state: RootState) => state.workingDirectories.workingDirectories);
    const currentDirectory = useSelector((state: RootState) => state.workingDirectories.workingDirectories.length > 0 ? state.workingDirectories.workingDirectories[state.workingDirectories.workingDirectories.length - 1] : null);
    const siteInfo = useSelector((state: RootState) => state.tenantInfo.sites);
    const enableNewTemplateFilters = useSelector((state: RootState) => state.tenantInfo.enableNewTemplateFilters);
    const newTemplateDocumentSetFilters = useSelector((state: RootState) => state.tenantInfo.newTemplateDocumentSetFilters);
    const newTemplateFolderFilters = useSelector((state: RootState) => state.tenantInfo.newTemplateFolderFilters);
    const newTemplateLibraryFilters = useSelector((state: RootState) => state.tenantInfo.newTemplateLibraryFilters);
    const enforceStrictDocumentSetsOnly = useSelector((state: RootState) => state.tenantInfo.enforceStrictDocumentSetsOnly);
    const cacheUpdated = useSelector((state: RootState) => state.cacheUpdate.toggle);
    const clientTypes = useClientTypeContext(currentDirectory?.siteId);

    const datasourceDocumentTemplates = React.useMemo(() => getDatasourceTemplates(), [cacheUpdated]);

    const menuItem_NewDatasourceDocumentTemplate = React.useMemo(() => {
        const folderPermissionMenuState = getFolderPermissionMenuState({
            currentSharepointFolder,
            requiredPermissions: ["canAddListItem", "canEditListItem"]
        });

        if (isEnabled
            && datasourceDocumentTemplates.length > 0
            && folderPermissionMenuState.shouldRender
            && !workingDirectories.some(x => x.type === ComponentTypes.SharePointList)) {

            var folderViewWrapper = new FolderViewWrapper(siteInfo, currentDirectory);
            const canViewFolder = folderViewWrapper.canViewFolder();

            const isInFolder = ((currentDirectory?.contentTypeId?.startsWith(SharePointFolderContentTypeIds.folder) && currentDirectory.serverRelativePath) || (currentDirectory?.directory?.length > 0 && currentDirectory.parentId));

            const currentContentype = getCurrentTemplateContentType({ currentDirectory });

            if (enableNewTemplateFilters) {
                if (!isTemplateFilterAllowed({
                    currentContentType: currentContentype,
                    filter: newTemplateFilterEnum.file,
                    newTemplateLibraryFilters,
                    newTemplateFolderFilters,
                    newTemplateDocumentSetFilters
                })) {
                    return null;
                }
            }

            if (enforceStrictDocumentSetsOnly
                && currentContentype === rootContentType
                && newDocumentTemplates?.some(template => template?.templateId?.startsWith(SharePointFolderContentTypeIds.documentSet))) {
                return null;
            }

            if (canViewFolder || (!canViewFolder && !isInFolder)) {
                const currentSite: string[] = [currentDirectory?.siteDisplayName ?? ""];
                const currentLibrary: string[] = [currentDirectory?.DocumentLibrary ?? ""];

                const dsTemplates: NewDocumentTemplate[] = datasourceDocumentTemplates
                    .filter(template => DatasourceFieldHelper.checkDatasourceAllowed(currentSite, currentLibrary, template.datasource, clientTypes));

                if (dsTemplates.length > 0) {
                    const { rootItems, groupedItems } = dsTemplates.reduce(
                        (acc, template) => {
                            const templateItem: IContextualMenuItem = {
                                key: `new-${template.templateId}`,
                                text: template.title,
                                iconProps: {},
                                onRenderIcon: () => <Icon {...getFileTypeIconProps({ extension: template.extention, size: 20, imageFileType: "svg" })} styles={iconStyles} />,
                                onClick: () => {
                                    runFolderPermissionMenuAction(folderPermissionMenuState, () => openCreateTemplateModal(template));
                                }
                            };

                            if (template.group) {
                                acc.groupedItems[template.group] = acc.groupedItems[template.group] || [];
                                acc.groupedItems[template.group].push(templateItem);
                            } else {
                                acc.rootItems.push(templateItem);
                            }

                            return acc;
                        },
                        { rootItems: [] as IContextualMenuItem[], groupedItems: {} as { [key: string]: IContextualMenuItem[] } }
                    );

                    const groupMenuItems: IContextualMenuItem[] = Object.keys(groupedItems)
                        .sort()
                        .map(group => ({
                            key: `group-${group}`,
                            text: group,
                            itemProps: rootItems.length > 0 ? { styles: { label: { marginLeft: 8 } } } : undefined,
                            subMenuProps: {
                                items: groupedItems[group]
                            }
                        }));

                    const menuItems: IContextualMenuItem[] = [...rootItems, ...groupMenuItems];

                    return {
                        key: "newDatasourceFileTemplate",
                        disabled: folderPermissionMenuState.isDisabled || !currentDirectory?.sharePointListId || waitFor === true || isBusySaving === true,
                        text: languageStrings.Templates,
                        // iconProps: { iconName: "FileTemplate" },
                        iconProps: {},
                        onRenderIcon: () => <DocumentAdd20Regular className={menuItemIconStyles} />,
                        subMenuProps: {
                            items: menuItems
                        }
                    } as IContextualMenuItem;
                }
            }
        }

        return null;

    }, [
        isEnabled, waitFor, newDocumentTemplates, currentSharepointFolder, workingDirectories, currentDirectory, siteInfo, datasourceDocumentTemplates,
        enableNewTemplateFilters, newTemplateLibraryFilters, newTemplateFolderFilters, newTemplateDocumentSetFilters,
        enforceStrictDocumentSetsOnly, isBusySaving, languageStrings, openCreateTemplateModal, clientTypes
    ]);

    return {
        menuItem_NewDatasourceDocumentTemplate
    }
}
